/**
 * 权限引擎 API
 * 包含：权限覆盖 CRUD、权限溯源、批量检查、审计日志
 * 对应 foundation-service (7090)
 */

import { unifiedApi } from '@/utils/unifiedApi';
import handleApiResponse from '@/utils/apiResponseHandler';
import type {
  PermissionOverrideInfo,
  PermissionOverrideQueryParams,
  CreatePermissionOverrideData,
  UpdatePermissionOverrideData,
  BatchCheckPermissionData,
  BatchCheckPermissionResult,
  TracePermissionData,
  TracePermissionResult,
  PermissionAuditLogInfo,
  PermissionAuditLogQueryParams,
} from './types';

// ===== 权限覆盖 CRUD =====

/** 获取权限覆盖列表 */
export const getPermissionOverrideList = async (params: PermissionOverrideQueryParams = {}) => {
  return handleApiResponse(
    unifiedApi.service('permission').get<{
      data: { list: PermissionOverrideInfo[]; total: number };
      count: number;
    }>('/permission-overrides', { params })
  );
};

/** 创建权限覆盖 */
export const createPermissionOverride = async (data: CreatePermissionOverrideData) => {
  return handleApiResponse(
    unifiedApi.service('permission').post<{ data: PermissionOverrideInfo; count: number }>(
      '/permission-overrides',
      data
    )
  );
};

/** 更新权限覆盖 */
export const updatePermissionOverride = async (id: number, data: UpdatePermissionOverrideData) => {
  return handleApiResponse(
    unifiedApi.service('permission').put<{ data: null; count: number }>(
      `/permission-overrides/${id}`,
      data
    )
  );
};

/** 删除权限覆盖 */
export const deletePermissionOverride = async (id: number) => {
  return handleApiResponse(
    unifiedApi.service('permission').delete<{ data: null; count: number }>(
      `/permission-overrides/${id}`
    )
  );
};

// ===== 批量权限检查 =====

/** 批量检查权限 */
export const batchCheckPermissions = async (data: BatchCheckPermissionData) => {
  return handleApiResponse(
    unifiedApi.service('permission').post<{ data: BatchCheckPermissionResult; count: number }>(
      '/permissions/check/batch',
      data
    )
  );
};

// ===== 权限溯源 =====

/** 权限溯源 */
export const tracePermission = async (data: TracePermissionData) => {
  return handleApiResponse(
    unifiedApi.service('permission').post<{ data: TracePermissionResult; count: number }>(
      '/permissions/trace',
      data
    )
  );
};

// ===== 权限审计日志 =====

/** 获取权限审计日志列表 */
export const getPermissionAuditLogs = async (params: PermissionAuditLogQueryParams = {}) => {
  return handleApiResponse(
    unifiedApi.service('permission').get<{
      data: { list: PermissionAuditLogInfo[]; total: number };
      count: number;
    }>('/permission-audit-logs', { params })
  );
};

/** 权限引擎 API 对象 */
export const permissionEngineApi = {
  getPermissionOverrideList,
  createPermissionOverride,
  updatePermissionOverride,
  deletePermissionOverride,
  batchCheckPermissions,
  tracePermission,
  getPermissionAuditLogs,
};

export default permissionEngineApi;
